import { useCallback, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";
import { errorMessage } from "../utils/errorMessage";
import type { UploadStatus } from "../pages/settings/components/DocumentUploadField";

export type DocKey = "identity" | "address" | "cac";

export type DocState = {
  file: File | null;
  status: UploadStatus;
  error?: string;
  /** Set once the PUT to storage succeeds. */
  url?: string;
};

export const DOCS: {
  key: DocKey;
  documentType: string;
  /** Key this document occupies in the business `documents` object. */
  field: "identity" | "proofOfAddress" | "cac";
  label: string;
  hint: string;
}[] = [
  {
    key: "identity",
    documentType: "IDENTITY",
    field: "identity",
    label: "Identity document",
    hint: "Merchant's valid government ID (e.g. Driver's license, Passport, Voter's card)",
  },
  {
    key: "address",
    documentType: "PROOF-OF-ADDRESS",
    field: "proofOfAddress",
    label: "Proof of address",
    hint: "Any valid document clearly displaying the merchant's address, no more than 4 months old (e.g. utility bill)",
  },
  {
    key: "cac",
    documentType: "CAC",
    field: "cac",
    label: "CAC document",
    hint: "Business registration certificate or CAC document",
  },
];

const INITIAL: Record<DocKey, DocState> = {
  identity: { file: null, status: "idle" },
  address: { file: null, status: "idle" },
  cac: { file: null, status: "idle" },
};

/**
 * The two-step document upload — ask the API for a signed URL, then PUT the
 * file straight to storage — shared by onboarding (auth/BusinessProfile via
 * UploadDocuments) and the settings Documents tab.
 *
 * `onUploaded`/`onCleared` let onboarding mirror the resulting URLs into
 * user-context, which is how BusinessProfile collects them for createBusiness.
 * Callers that don't need that can read `documents` off the returned state.
 */
export const useDocumentUploads = (options?: {
  onUploaded?: (key: DocKey, objectURL: string) => void;
  onCleared?: (key: DocKey) => void;
  onFileChange?: (key: DocKey, file: File | null) => void;
}) => {
  const axiosInstance = useAuthAxios();
  const [docs, setDocs] = useState<Record<DocKey, DocState>>(INITIAL);

  const setDoc = useCallback(
    (key: DocKey, patch: Partial<DocState>) =>
      setDocs((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } })),
    []
  );

  const upload = useCallback(
    async (key: DocKey, documentType: string, file: File) => {
      setDoc(key, { file, status: "uploading", error: undefined });
      options?.onFileChange?.(key, file);

      try {
        const { data } = await axiosInstance.post(API.requestDocumentUpload, {
          documentType,
        });

        // Plain axios, not the auth instance: the signed URL is pre-authorised and must
        // not receive our session cookie or any auth header.
        await axios.put(data.data.signedURL, file, {
          headers: { "Content-Type": file.type },
        });

        setDoc(key, { status: "uploaded", url: data.data.objectURL });
        options?.onUploaded?.(key, data.data.objectURL);
      } catch (e: any) {
        const message = errorMessage(
          e?.response?.data?.message || e?.message || "Upload failed"
        );
        setDoc(key, { status: "error", error: message, url: undefined });
        options?.onUploaded?.(key, "");
        toast.error(message);
      }
    },
    // axiosInstance is a fresh object every render, so it stays out of the deps —
    // same as use-business-image-upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDoc]
  );

  const clear = useCallback(
    (key: DocKey) => {
      setDoc(key, {
        file: null,
        status: "idle",
        error: undefined,
        url: undefined,
      });
      options?.onFileChange?.(key, null);
      options?.onCleared?.(key);
      options?.onUploaded?.(key, "");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDoc]
  );

  const allUploaded = DOCS.every(({ key }) => docs[key].status === "uploaded");
  const anyUploading = DOCS.some(({ key }) => docs[key].status === "uploading");
  const uploadedCount = DOCS.filter(
    ({ key }) => docs[key].status === "uploaded"
  ).length;

  /** Shaped for the business `documents` field. */
  const documents = DOCS.reduce<Record<string, string>>((acc, doc) => {
    const url = docs[doc.key].url;
    if (url) acc[doc.field] = url;
    return acc;
  }, {});

  return {
    docs,
    upload,
    clear,
    allUploaded,
    anyUploading,
    uploadedCount,
    documents,
    reset: () => setDocs(INITIAL),
  };
};
